import { stripOidcLandQueryParam } from '../session/returnTarget.js';
import { isCustomerRecoveryPath } from './isCustomerRecoveryPath.js';

/** Bound nested recovery?returnTo=… peeling (encoded chains). */
const MAX_RECOVERY_RETURN_UNWRAP = 8;

function splitPathAndSearch(pathWithQuery: string): {
  pathname: string;
  search: string;
} {
  const qIndex = pathWithQuery.indexOf('?');
  if (qIndex === -1) {
    return { pathname: pathWithQuery, search: '' };
  }
  return {
    pathname: pathWithQuery.slice(0, qIndex),
    search: pathWithQuery.slice(qIndex),
  };
}

/**
 * Peel `returnTo` until the target is not a recovery route.
 * Recovery → recovery nesting creates unusable resume URLs and L1 loops.
 */
export function unwrapNonRecoveryReturnTo(
  pathWithQuery: string,
): string | undefined {
  let current = stripOidcLandQueryParam(pathWithQuery);
  for (let i = 0; i < MAX_RECOVERY_RETURN_UNWRAP; i += 1) {
    if (current.length === 0) {
      return undefined;
    }
    const { pathname, search } = splitPathAndSearch(current);
    if (!isCustomerRecoveryPath(pathname)) {
      return current;
    }
    const nested = new URLSearchParams(
      search.startsWith('?') ? search.slice(1) : search,
    ).get('returnTo');
    if (nested === null || nested.length === 0) {
      return undefined;
    }
    current = stripOidcLandQueryParam(nested);
  }
  return undefined;
}

/**
 * Capture a safe resume target from the current location.
 * Never returns a `/:tenant/recovery/...` path (unwraps nested returnTo when present).
 */
export function captureRecoveryReturnTo(
  pathname: string,
  search: string = '',
): string | undefined {
  return unwrapNonRecoveryReturnTo(`${pathname}${search}`);
}
